import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Loading from "../common/Loading";
import { ACCENT, PrimaryButton, SecondaryButton, formatDuration } from "./shared";
import useStudioAccount from "./useStudioAccount";
import StudioGate from "./StudioGate";
import TrackStatusChip from "./TrackStatusChip";
import { AI_OPTIONS } from "./TrackUpload";
import { EditTrackDialog, TakeDownDialog } from "./TrackDialogs";
import { AppealDialog, DeleteTrackDialog } from "./TrackAppealDialog";
import { MESSAGES, trackApi, trackErrorMessage } from "./trackApi";
import TrackPerformance from "./TrackPerformance";
import TrackPromotion from "./TrackPromotion";
import Waveform from "../soundDetail/Waveform";

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

// "FLAC · 48 kHz", or "MP4 · 1280×720 · AAC" for a video. Built only from what the server really read.
export function formatLabel(track) {
  const a = track?.audio?.analysis;
  if (!a) return "—";
  const parts = [];
  if (a.container) parts.push(String(a.container).split("/")[0].toUpperCase());
  if (a.width && a.height) parts.push(`${a.width}×${a.height}`);
  if (a.codec) parts.push(String(a.codec).replace(/^MPEG-4\//, ""));
  if (a.sample_rate) parts.push(`${Number((a.sample_rate / 1000).toFixed(1))} kHz`);
  return parts.length ? parts.join(" · ") : "—";
}

const FINGERPRINT = {
  pending: "Pending",
  scanning: "Scan in progress",
  completed: "Clear, no conflicts found",
  failed: "Scan failed",
};

// A release date after today is a scheduled release. The calendar date is compared in local time: parsing
// "YYYY-MM-DD" with new Date() gives UTC midnight, which is a day off for anyone east of UTC (e.g. India).
export const isScheduled = (value, now = new Date()) => {
  if (!value) return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return d.getTime() > today.getTime();
};

const formatMoney = (value) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", minimumFractionDigits: Number.isInteger(value) ? 0 : 2 }).format(value);

const metric = (value) => (value === null || value === undefined ? "—" : Number(value).toLocaleString());

const QUALITY = { standard: "Standard", high_fidelity: "High-Fidelity (lossless)" };

const PROCESSING = {
  ready_for_review: { label: "Processed", tone: "bg-green-100 text-green-800" },
  processing: { label: "Processing", tone: "bg-amber-100 text-amber-800" },
  uploaded: { label: "Waiting to be checked", tone: "bg-blue-100 text-blue-800" },
  uploading: { label: "Uploading", tone: "bg-blue-100 text-blue-800" },
  processing_failed: { label: "Processing failed", tone: "bg-red-100 text-red-800" },
  upload_failed: { label: "Upload failed", tone: "bg-red-100 text-red-800" },
};

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
    </div>
  );
}

function Item({ label, children }) {
  return (
    <div className="min-w-0">
      <p className="text-sm text-gray-500">{label}</p>
      <div className="font-semibold text-gray-900 mt-0.5 break-words">{children || "—"}</div>
    </div>
  );
}

function DetailBody() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [track, setTrack] = useState(null);
  const [artistName, setArtistName] = useState("");
  const [error, setError] = useState("");
  const [asking, setAsking] = useState(false);
  const [editingLive, setEditingLive] = useState(false);
  const [appealing, setAppealing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([trackApi.detail(id), trackApi.rules().catch(() => null)])
      .then(([t, rules]) => {
        if (cancelled) return;
        setArtistName(rules?.artist_name || "");
        setTrack(t);
      })
      .catch((e) => !cancelled && setError(trackErrorMessage(e, MESSAGES[500])));
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (error) {
    return (
      <p className="rounded-xl bg-red-50 border border-red-200 text-red-800 px-4 py-3" role="alert">
        {error} <Link to="/artist-studio/tracks" className="underline font-semibold">Back to catalogue</Link>
      </p>
    );
  }
  if (!track) {
    return (
      <div className="flex justify-center py-16">
        <Loading />
      </div>
    );
  }

  const processing = PROCESSING[track.upload_status];
  const aiLabel = AI_OPTIONS.find((o) => o.value === track.ai_disclosure)?.title;
  const canTakeDown = track.can_take_down ?? ["published", "pending_review", "approved"].includes(track.display_status);
  const editTo = `/artist-studio/tracks/${track.id}`;
  const pricingTo = `/artist-studio/monetization?track=${track.id}`;
  const metrics = track.metrics || {};
  const quality = track.content_type === "video" ? null : QUALITY[track.audio_quality] || QUALITY.standard;
  const formatText = formatLabel(track);
  const formatAndQuality = [formatText === "—" ? null : formatText, quality].filter(Boolean).join(" · ");
  // A likely match found by the audio check is not shown as clear; the artist is not told which track it matches.
  const fingerprint = track.fingerprint_flagged && track.fingerprint_status === "completed"
    ? "Needs an extra check, a moderator will review it"
    : FINGERPRINT[track.fingerprint_status] || FINGERPRINT.pending;
  const scheduled = isScheduled(track.release_date);
  const editLabel = track.can_edit || track.can_edit_live ? "Edit Metadata" : null;
  const onEdit = () => (track.can_edit ? navigate(editTo) : setEditingLive(true));

  const cancelReplacement = async () => {
    if (busy) return;
    setBusy(true);
    try {
      setTrack(await trackApi.cancelAudioReplacement(track.id));
      toast.success("The replacement audio was cancelled. Your current audio is unchanged.");
    } catch (e) {
      toast.error(trackErrorMessage(e, MESSAGES[500]));
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await trackApi.remove(track.id);
      toast.success(`“${track.title}” was deleted.`);
      navigate("/artist-studio/tracks");
    } catch (e) {
      toast.error(trackErrorMessage(e, MESSAGES[500]));
      setBusy(false);
      setDeleting(false);
    }
  };

  const confirmTakeDown = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const updated = await trackApi.takeDown(track.id);
      setTrack(updated);
      setAsking(false);
      toast.success(updated.display_status === "archived" ? `“${track.title}” was taken down. Existing licences remain valid.` : "The track was withdrawn from review.");
    } catch (e) {
      toast.error(trackErrorMessage(e, MESSAGES[500]));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="track-detail">
      <Link to="/artist-studio/tracks" className="text-sm font-semibold text-gray-700 hover:underline">← Back to catalogue</Link>

      <div className="flex flex-col sm:flex-row gap-6 mt-5">
        <div className="shrink-0 text-center">
          <div className="w-48 h-48 rounded-3xl bg-[#e2dccf] overflow-hidden flex items-center justify-center text-gray-500">
            {track.artwork?.url
              ? <img src={track.artwork.url} alt="" className="w-full h-full object-cover" />
              : <span className="text-5xl" aria-hidden="true">♪</span>}
          </div>
          {track.can_change_cover && (
            <Link to={editTo} className="inline-block mt-2 text-sm font-semibold" style={{ color: ACCENT }}>Change cover</Link>
          )}
        </div>
        <div className="self-end pb-8">
          <h2 className="text-4xl font-bold text-gray-900">{track.title || "Untitled draft"}</h2>
          <p className="font-semibold mt-2" style={{ color: ACCENT }}>{artistName}</p>
          <p className="text-gray-500 mt-1">
            {track.release_name ? `${track.release_name} · ` : ""}Uploaded {formatDate(track.created_at)}
          </p>
          <div className="mt-3"><TrackStatusChip status={track.display_status} /></div>
        </div>
      </div>

      {track.status === "rejected" && (
        <section className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-red-900" role="alert" aria-label="Moderation outcome" data-testid="rejection-box">
          <p className="font-semibold">This track was rejected</p>
          <p className="mt-1 text-sm" data-testid="rejection-reason">{track.rejection_reason || "No reason was recorded."}</p>
          <p className="mt-2 text-sm text-red-800" data-testid="rejection-next">
            {track.can_resubmit
              ? "You can correct the rights information and contributor details and resubmit this track once."
              : track.appeal_status === "pending"
                ? "Your appeal is waiting for a decision. We will email you and notify you in the app."
                : track.appeal_status === "denied"
                  ? "Your appeal was denied. This decision is final, and the track can only be deleted."
                  : track.can_appeal
                    ? "Your one resubmission was rejected as well. You can appeal this decision once."
                    : "This track cannot be resubmitted right now."}
          </p>
        </section>
      )}
      {track.appeal && (
        <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-6" aria-label="Appeal" data-testid="appeal-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-lg font-bold text-gray-900">Appeal</h3>
            <span
              className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${
                track.appeal.status === "approved" ? "bg-green-100 text-green-800" : track.appeal.status === "denied" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"
              }`}
              data-testid="appeal-status"
            >
              {track.appeal.status === "approved" ? "Approved" : track.appeal.status === "denied" ? "Denied" : "Under review"}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Filed {formatDate(track.appeal.filed_at)}{track.appeal.case_number ? ` · Case ${track.appeal.case_number}` : ""}
          </p>
          <p className="mt-3 whitespace-pre-wrap break-words text-sm text-gray-800">{track.appeal.reason}</p>
          {track.appeal.evidence.length > 0 && (
            <ul className="mt-3 space-y-1 text-sm">
              {track.appeal.evidence.map((file, i) => (
                <li key={`${file.url}-${i}`}><a href={file.url} target="_blank" rel="noreferrer" className="font-semibold underline">{file.name || "Evidence"}</a></li>
              ))}
            </ul>
          )}
          {track.appeal.decision_note && (
            <p className="mt-3 rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-800">
              <span className="font-semibold">Decision{track.appeal.decided_at ? ` (${formatDate(track.appeal.decided_at)})` : ""}:</span> {track.appeal.decision_note}
            </p>
          )}
        </section>
      )}

      {track.pending_audio && (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-amber-900 flex flex-wrap items-center justify-between gap-3" data-testid="pending-audio">
          <p className="text-sm">
            <span className="font-semibold">New audio waiting for review:</span> {track.pending_audio.file_name}. Listeners and licensees keep getting the current audio until it is approved.
          </p>
          <button type="button" className="text-sm font-semibold underline disabled:opacity-50" disabled={busy} onClick={cancelReplacement} data-testid="cancel-replacement">
            Cancel replacement
          </button>
        </div>
      )}
      {track.audio_replacement_rejection && (
        <p className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-800" role="alert" data-testid="replacement-rejected">
          <span className="font-semibold">Your replacement audio “{track.audio_replacement_rejection.file_name}” was not approved:</span> {track.audio_replacement_rejection.reason} The current audio is unchanged.
        </p>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Stat label="Streams" value={metric(metrics.streams)} />
        <Stat label="Likes" value={metric(metrics.likes)} />
        <Stat label="Licenses" value={metric(metrics.licenses)} />
        <Stat label="Earnings" value={metrics.earnings === null || metrics.earnings === undefined ? "—" : `${formatMoney(metrics.earnings)}${Number(metrics.earnings_pending) > 0 ? ` (${formatMoney(metrics.earnings_pending)} pending)` : ""}`} />
      </div>

      {["published", "archived"].includes(track.status) && <TrackPerformance trackId={track.id} />}
      {["published", "archived"].includes(track.status) && <TrackPromotion trackId={track.id} />}

      <section className="rounded-2xl border border-gray-200 bg-white p-6 mt-6" aria-label="Track details">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Track details</h3>
        <div className="grid sm:grid-cols-3 gap-x-8 gap-y-5">
          <Item label="Artist">{artistName}</Item>
          <Item label="Genre">{track.genre?.name}</Item>
          <Item label="Release">{track.release_name}</Item>
          <Item label="Release date">{track.release_date ? `${formatDate(track.release_date)}${scheduled ? " (scheduled)" : ""}` : null}</Item>
          <Item label={track.content_type === "video" ? "Uploaded video file" : "Uploaded audio file"}>{track.audio?.file_name}</Item>
          <Item label={track.content_type === "video" ? "Video format" : "Audio format and quality"}>{formatAndQuality}</Item>
          <Item label="File duration">{formatDuration(track.duration)}</Item>
          <Item label="Processing status">
            {processing && (
              <span className={`inline-block rounded-full px-3 py-1 text-sm font-semibold ${track.fingerprint_status === "scanning" ? "bg-amber-100 text-amber-800" : processing.tone}`}>
                {track.fingerprint_status === "scanning" ? "Processing — fingerprint scan" : processing.label}
              </span>
            )}
          </Item>
        </div>
        {track.content_type !== "video" && (track.audio?.check?.warnings?.length > 0 || track.audio?.waveform?.length > 0) && (
          <div className="mt-6 border-t border-gray-100 pt-5" data-testid="audio-check">
            <p className="text-sm font-semibold text-gray-900 mb-2">Audio check</p>
            {track.audio?.waveform?.length > 0 && <Waveform peaks={track.audio.waveform} />}
            {track.audio?.check?.warnings?.length > 0 && (
              <ul className="mt-3 space-y-1">
                {track.audio.check.warnings.map((w) => (
                  <li key={w.code || w} className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    {w.message || w}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-6 mt-6" aria-label="Metadata and rights">
        <h3 className="text-lg font-bold text-gray-900 mb-3">Metadata &amp; rights</h3>
        <ul className="space-y-2 text-gray-700">
          <li><span className="text-gray-500">Sole-ownership declaration — </span>{track.ownership_confirmed ? "Confirmed by artist" : "Not confirmed"}</li>
          <li><span className="text-gray-500">AI disclosure — </span>{aiLabel || "Not provided"}</li>
          <li><span className="text-gray-500">Fingerprint status — </span>{fingerprint}</li>
          {(track.composer || track.lyricist) && (
            <li>
              <span className="text-gray-500">Contributors — </span>
              {[track.composer && `Composer: ${track.composer}`, track.lyricist && `Lyricist: ${track.lyricist}`].filter(Boolean).join(" · ")}
            </li>
          )}
          {track.ownership_confirmed && (
            <li><span className="text-gray-500">Contributor release waiver — </span>Covered by the sole-ownership declaration</li>
          )}
        </ul>
      </section>

      <div className="flex flex-wrap gap-3 mt-6">
        {editLabel ? (
          <PrimaryButton onClick={onEdit} data-testid="edit-metadata">{editLabel}</PrimaryButton>
        ) : (
          <PrimaryButton
            disabled data-testid="edit-metadata"
            title={track.status === "rejected" ? "This track can no longer be changed." : "This track can't be edited while it is being processed or reviewed."}
          >
            Edit Metadata
          </PrimaryButton>
        )}
        {track.can_resubmit && (
          <PrimaryButton onClick={() => navigate(`${editTo}?resubmit=1`)} data-testid="resubmit-track">Resubmit Track</PrimaryButton>
        )}
        {track.can_appeal && (
          <PrimaryButton onClick={() => setAppealing(true)} data-testid="appeal-track">Appeal Decision</PrimaryButton>
        )}
        <SecondaryButton onClick={() => navigate(pricingTo)} data-testid="set-pricing">Set Pricing</SecondaryButton>
        {track.display_status === "archived" && (
          <p className="self-center text-gray-500" data-testid="taken-down-note">Taken down — no longer visible or licensable on Nyawoo</p>
        )}
        {track.can_delete && (
          <button
            type="button"
            onClick={() => setDeleting(true)}
            className="rounded-full font-semibold px-7 py-3 border border-red-500 text-red-600 bg-white hover:bg-red-50"
            data-testid="delete-track"
          >
            Delete Track
          </button>
        )}
        {canTakeDown && (
          <button
            type="button"
            onClick={() => setAsking(true)}
            className="rounded-full font-semibold px-7 py-3 border border-red-500 text-red-600 bg-white hover:bg-red-50"
            data-testid="take-down"
          >
            Take Down
          </button>
        )}
      </div>

      {editingLive && (
        <EditTrackDialog
          track={track}
          onCancel={() => setEditingLive(false)}
          onSaved={(updated) => {
            setTrack(updated);
            setEditingLive(false);
          }}
        />
      )}
      {appealing && (
        <AppealDialog
          track={track}
          onCancel={() => setAppealing(false)}
          onSubmitted={(updated) => {
            setTrack(updated);
            setAppealing(false);
            toast.success("Your appeal was submitted. We will email you and notify you in the app once it has been reviewed.");
          }}
        />
      )}
      {deleting && <DeleteTrackDialog track={track} busy={busy} onCancel={() => !busy && setDeleting(false)} onConfirm={confirmDelete} />}
      {asking && <TakeDownDialog track={track} busy={busy} onCancel={() => !busy && setAsking(false)} onConfirm={confirmTakeDown} />}
    </div>
  );
}

export default function TrackDetail() {
  const account = useStudioAccount();
  return (
    <StudioGate account={account} active="tracks" heading="" tabs={[]} maxWidth="max-w-4xl">
      <DetailBody />
    </StudioGate>
  );
}
